import { match } from 'ts-pattern'
import { Plan, PlanInfo, PlanInfoMap } from '../types'
import enterprisePlanBg from '~/assets/enterprise_bg.jpg'
import plusPlanBg from '~/assets/plus_bg.jpg'
import teamPlanBg from '~/assets/team_bg.jpg'
import { COMMUNITY_PLAN_PRODUCT_ID, PLUS_PLAN_PRODUCT_ID, TEAM_PLAN_PRODUCT_ID } from '../constants'

export const planOrder = ['community', 'plus', 'team', 'enterprise', 'managed'] as const

export function isPaywalled(currentPlan: Plan, minPlan: Plan): boolean {
  return planOrder.indexOf(currentPlan) < planOrder.indexOf(minPlan)
}

export const isUpgrade = (currentPlan: Plan, desiredPlan: Plan) => {
  return planOrder.indexOf(currentPlan) < planOrder.indexOf(desiredPlan)
}

export const getLegacyNameFromPlanId = (planId: string): Plan => {
  if (planId === COMMUNITY_PLAN_PRODUCT_ID) {
    return 'community'
  }
  if (planId === PLUS_PLAN_PRODUCT_ID) {
    return 'plus'
  }
  if (planId === TEAM_PLAN_PRODUCT_ID) {
    return 'team'
  }

  return 'community'
}

export const getOrderedPlansInfo = () => {
  return planOrder
    .filter((plan) => plan !== 'managed')
    .reduce((acc: PlanInfoMap, plan) => {
      acc[plan] = getPlanInfo(plan)
      return acc
    }, {} as PlanInfoMap)
}

export const MANAGED_PLAN_INFO: PlanInfo = {
  productId: 'managed-plan',
  displayName: 'Managed',
  legacyName: 'managed' as const,
  backgroundUrl: enterprisePlanBg,
  features: [],
  description: 'Botpress will build, maintain, and optimize your agent on a flexible commitment term.',
  priceSuffix: '/mo',
  previousPlan: 'enterprise' as const,
}

const getPlanInfo = (legacyPlanName: Exclude<Plan, 'managed'>): PlanInfo => {
  return match(legacyPlanName)
    .with('community', () => ({
      productId: COMMUNITY_PLAN_PRODUCT_ID,
      displayName: 'Pay-as-you-go',
      legacyName: 'community' as const,
      backgroundUrl: undefined,
      features: ['Visual editing studio', 'Free monthly AI Credit', 'Community support'],
      addons: [
        { quotaType: 'invocation_calls', display: '500 messages & events', value: 500 } as const,
        { quotaType: 'table_row_count', display: '1,000 tables rows', value: 1_000 } as const,
        { quotaType: 'bot_count', display: '1 bot', value: 1 } as const,
        { quotaType: 'workspace_member_count', display: '1 member only', value: 1 } as const,
        { quotaType: 'knowledgebase_vector_storage', display: '100MB vector DB storage', value: 0.1 } as const,
        { quotaType: 'storage_count', display: '100MB storage', value: 0.1 } as const,
      ],
      description: 'For hobbyists and builders looking to develop a prototype AI agent.',
      priceSuffix: '/mo + AI Spend',
      previousPlan: undefined,
    }))
    .with('plus', () => ({
      productId: PLUS_PLAN_PRODUCT_ID,
      displayName: 'Plus',
      legacyName: 'plus' as const,
      backgroundUrl: plusPlanBg,
      features: ['Visual KB indexing', 'Live agent handoff', 'Remove Botpress logo', 'Live-chat support'],
      addons: [
        { quotaType: 'invocation_calls', display: '5,000 messages & events', value: 5_000 } as const,
        { quotaType: 'table_row_count', display: '100,000 tables rows', value: 100_000 } as const,
        { quotaType: 'bot_count', display: '2 bots', value: 2 } as const,
        { quotaType: 'always_alive', display: 'Always alive 1 bot', value: 1 } as const,
        { quotaType: 'workspace_member_count', display: '1 collaborator seat', value: 2 } as const,
        { quotaType: 'knowledgebase_vector_storage', display: '1GB vector DB storage', value: 1 } as const,
        { quotaType: 'storage_count', display: '10GB storage', value: 10 } as const,
      ],
      description: 'A flexible plan ideal for teams scaling their agent projects.',
      priceSuffix: '/mo + AI Spend',
      previousPlan: 'community' as const,
    }))
    .with('team', () => ({
      productId: TEAM_PLAN_PRODUCT_ID,
      displayName: 'Team',
      legacyName: 'team' as const,
      backgroundUrl: teamPlanBg,
      features: ['RBAC', 'Real-time collaboration', 'Bot revisions', 'Advanced support'],
      addons: [
        { quotaType: 'invocation_calls', display: '50,000 messages & events', value: 50_000 } as const,
        { quotaType: 'table_row_count', display: '100,000 tables rows', value: 100_000 } as const,
        { quotaType: 'bot_count', display: '3 bots', value: 3 } as const,
        { quotaType: 'always_alive', display: 'Always Alive 3 bots', value: 3 } as const,
        { quotaType: 'workspace_member_count', display: '2 collaborator seats', value: 3 } as const,
        { quotaType: 'knowledgebase_vector_storage', display: '2GB vector DB storage', value: 2 } as const,
        { quotaType: 'storage_count', display: '10GB storage', value: 10 } as const,
      ],
      description: 'For teams with mature agent projects.',
      priceSuffix: '/mo + AI Spend',
      previousPlan: 'plus' as const,
    }))
    .with('enterprise', () => ({
      productId: 'enterprise-plan',
      displayName: 'Managed',
      legacyName: 'enterprise' as const,
      backgroundUrl: enterprisePlanBg,
      features: [
        'Custom development',
        'Ongoing maintenance',
        'System integration',
        'Custom programmatic and AI workflows',
        'Proactive optimization recommendations',
        'Priority support',
        'Regular performance reports',
        'Monthly strategy calls',
        'Team training sessions',
      ],
      description: 'Botpress will build, maintain, and optimize your agent on a flexible commitment term.',
      priceSuffix: '/mo',
      previousPlan: 'team' as const,
    }))
    .exhaustive()
}
